"use client";

import { Review } from "@/lib/types/review";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useRef } from "react";
import { ReviewCard } from "../review-card";
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface ReviewsScrollingCarouselProps {
  reviews: Review[];
}

function ReviewsScrollingCarousel({ reviews }: ReviewsScrollingCarouselProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLDivElement>(null);
  const reviewCardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(
    () => {
      const cards = reviewCardRefs.current.filter(Boolean) as HTMLDivElement[];
      if (cards.length < reviews.length) return;

      const headingDimensions = pinnedRef.current
        ?.querySelector("h2")
        ?.getBoundingClientRect();
      if (!headingDimensions) return;

      // Start all cards off-screen to the right
      gsap.set(cards, { xPercent: 105 });
      gsap.set(sectionRef.current, {
        height: window.innerHeight * reviews.length,
      });
      gsap.set(pinnedRef.current, {
        height: window.innerHeight - headingDimensions.height,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: 0.7,
          pin: pinnedRef.current,
          pinType: "transform",
        },
      });

      cards.forEach((card, i) => {
        // Slide each card into its natural position
        tl.to(card, { xPercent: 0, ease: "power2.out", duration: 1 });
        // Brief pause between cards (not after the last)
        if (i < cards.length - 1) {
          tl.to({}, { duration: 0.3 });
        }
      });
    },
    { scope: sectionRef },
  );

  return (
    <div className="w-full pt-16 2xl:pt-18" ref={sectionRef}>
      <div
        ref={pinnedRef}
        className="pinned-reviews w-full flex flex-col pt-3 border-t-2 border-t-primary 2xl:pt-5 will-change-transform"
      >
        <h2 className="section-heading font-bold text-3xl md:text-4xl 2xl:text-7xl">
          What People Say
        </h2>
        {/* Relative container clips cards entering from the right */}
        <div className="relative flex-1 overflow-hidden">
          {reviews.map((review, index) => (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ zIndex: index }}
              key={review._id}
              ref={(el) => {
                reviewCardRefs.current[index] = el;
              }}
            >
              <ReviewCard review={review} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { ReviewsScrollingCarousel };
